import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';

const EntityFormSelector = ({ companyId }) => {
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadAvailableForms();
    }, []);

    const loadAvailableForms = async () => {
        try {
            const response = await fetch('/api/v1/dynamic-forms/forms');
            if (!response.ok) throw new Error('Failed to load forms');
            const data = await response.json();
            setForms(data.forms || []);
        } catch (err) {
            setError(`Failed to load forms: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleFormSelect = (form) => {
        // For now, we'll use a placeholder companyEntityId
        // In a real app, you'd get this from the company's entity relationships
        const companyEntityId = 1; // This should come from your company-entity relationship
        navigate(`/entity-profile/${form.entity_id}/${companyEntityId}`);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Loading available forms...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600">{error}</p>
                <Button onClick={loadAvailableForms} className="mt-2">
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-center">
                <h1 className="text-3xl font-bold mb-2">Entity Profile Forms</h1>
                <p className="text-gray-600">
                    Select a form to complete your entity profile information
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {forms.map((form) => (
                    <Card key={form.id} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-lg">{form.form_name}</CardTitle>
                                <Badge variant="secondary">v{form.form_version}</Badge>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    Complete your {form.form_name.toLowerCase()} profile to provide detailed information about your organization.
                                </p>

                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">
                                        Entity ID: {form.entity_id}
                                    </span>
                                    <Button
                                        onClick={() => handleFormSelect(form)}
                                        className="text-sm"
                                    >
                                        Fill Form
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {forms.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-gray-500">No forms available at this time.</p>
                </div>
            )}
        </div>
    );
};

export default EntityFormSelector;
